import { getLocalStorageItem } from 'base/localstorage';
import { Button, ButtonPalette, RadioButtonGroup } from 'base/ui/button';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { DividerLine } from 'base/ui/panel';
import { TextRegular, TextSmallLight, Title1 } from 'base/ui/typography';
import { zodParse } from 'base/zod_parse';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import {
  DEFAULT_FEATURE_FLAGS,
  FeatureFlags,
  featureFlagsSchema,
} from './blocks_service';

export const FLAG_KEYS = Object.keys(
  DEFAULT_FEATURE_FLAGS,
) as (keyof typeof DEFAULT_FEATURE_FLAGS)[];

type FeatureFlagsKey = keyof typeof DEFAULT_FEATURE_FLAGS;

const overrideStorageKey = 'flagOverrides';

export class FeatureFlagsStore {
  private flags = observable.map<FeatureFlagsKey, boolean>();
  overrides = observable.map<FeatureFlagsKey, boolean | undefined>();

  constructor(flags: FeatureFlags) {
    this.flags.replace(flags);
    this.loadFromLocalStorage();

    addEventListener('storage', (e) => {
      if (e.key === overrideStorageKey) {
        this.loadFromLocalStorage();
      }
    });
  }

  sync(flags: FeatureFlags) {
    this.flags.replace(flags);
  }

  updateOverride(key: FeatureFlagsKey, value: boolean | undefined) {
    if (value !== undefined) {
      this.overrides.set(key, value);
    } else {
      this.overrides.delete(key);
    }
    this.writeToLocalStorage();
  }

  clearAllOverrides() {
    this.overrides.clear();
    this.writeToLocalStorage();
  }

  private loadFromLocalStorage() {
    const v = getLocalStorageItem(overrideStorageKey);
    this.overrides.replace(v ? JSON.parse(v) : {});
  }

  private writeToLocalStorage() {
    localStorage.setItem(
      overrideStorageKey,
      JSON.stringify(Object.fromEntries(this.overrides.entries())),
    );
  }

  get(key: FeatureFlagsKey): boolean {
    return (
      this.overrides.get(key) ??
      this.flags.get(key) ??
      DEFAULT_FEATURE_FLAGS[key]
    );
  }
}

export const cacheStorageKey = 'featureFlagCache';
export function readFeatureFlagCache(): FeatureFlags {
  const cacheValue = getLocalStorageItem(cacheStorageKey);
  if (cacheValue) {
    try {
      return zodParse(
        featureFlagsSchema,
        cacheValue ? JSON.parse(cacheValue) : undefined,
      );
    } catch (e) {
      console.warn('Error reading feature flag cache:', e);
    }
  }
  return DEFAULT_FEATURE_FLAGS;
}
export function writeFeatureFlagCache(flags: FeatureFlags) {
  localStorage.setItem(cacheStorageKey, JSON.stringify(flags));
}

export const FlagOverridesPage = observer(function _FlagOverridesPage() {
  const featureFlagsStore = new FeatureFlagsStore(DEFAULT_FEATURE_FLAGS);

  return (
    <Rows spacing={16} xalign="center">
      <Title1>Flag overrides</Title1>
      <Button
        onClick={() => featureFlagsStore.clearAllOverrides()}
        label="Clear all"
        disabled={featureFlagsStore.overrides.size === 0}
        tooltip={
          featureFlagsStore.overrides.size === 0
            ? 'Can not clear as no overrides are set'
            : ''
        }
      />

      <DividerLine />
      <Rows spacing={8}>
        {FLAG_KEYS.map((k) => (
          <Toggle
            key={k}
            label={k}
            value={featureFlagsStore.overrides.get(k)}
            onChange={(v) => featureFlagsStore.updateOverride(k, v)}
          />
        ))}
      </Rows>
      <TextSmallLight>
        Remember to reload the editor after changing flags
      </TextSmallLight>
      <Button href="/" label="Back home" />
    </Rows>
  );
});

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | undefined;
  onChange(v: boolean | undefined): void;
}) {
  return (
    <>
      <Inline spacing={4} yalign="center">
        <TextRegular>{label}</TextRegular>
        <SpacerGrow />
        <ButtonPalette isDarker>
          <RadioButtonGroup
            options={[
              { label: 'On', value: true },
              { label: 'Off', value: false },
              { label: 'Default', value: undefined },
            ]}
            value={value}
            onChange={onChange}
          />
        </ButtonPalette>
      </Inline>
      <DividerLine />
    </>
  );
}
